<template>
  <nav class="navbar">
    <router-link to="/" class="brand">
      <span class="brand-mark">U80</span>
      <span class="brand-name">Unwritten 80%</span>
    </router-link>
    <div class="links">
      <span v-if="collegeLabel" class="session" :title="collegeLabel">{{ collegeLabel }}</span>
      <router-link v-if="isAdmin" to="/admin" class="nav-link">Admin</router-link>
      <router-link v-if="isAdmin" to="/admin/colleges" class="nav-link">Colleges</router-link>
      <router-link v-if="isAdmin" to="/admin/users" class="nav-link">Users</router-link>
      <router-link
        v-if="isAdmin"
        to="/admin/guides"
        class="nav-link nav-guide"
        title="Guides"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path
            d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v16H6.5A2.5 2.5 0 0 0 4 21.5V5.5Z"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
          />
          <path
            d="M8 7h8M8 11h8M8 15h5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
          />
        </svg>
        Guides
      </router-link>
      <router-link v-if="authenticated" to="/select-college" class="nav-link">College</router-link>
      <button
        v-if="authenticated"
        class="btn-logout"
        :disabled="signingOut"
        @click="logout"
      >
        {{ signingOut ? 'Signing out…' : 'Sign out' }}
      </button>
    </div>
  </nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { getSession, signOutAndLeave } from '../services/auth'
import { getAdminSession } from '../services/adminSession'
import { checkAdmin, getMe } from '../services/api'

const route = useRoute()
const authenticated = ref(false)
const isAdmin = ref(false)
const collegeLabel = ref('')
const signingOut = ref(false)

async function refresh() {
  const session = await getSession()
  authenticated.value = !!session
  collegeLabel.value = ''
  if (!session) {
    isAdmin.value = false
    return
  }
  try {
    const res = await checkAdmin()
    isAdmin.value = !!res.isAdmin
  } catch {
    isAdmin.value = !!session.isAdmin
  }
  try {
    if (isAdmin.value) {
      const viewing = getAdminSession()
      if (viewing.collegeName) {
        collegeLabel.value = viewing.collegeName
      }
    } else {
      const me = await getMe()
      if (me.collegeName) {
        collegeLabel.value = me.collegeName
      }
    }
  } catch {
    /* ignore */
  }
}

async function logout() {
  if (signingOut.value) return
  // Token revocation can take a moment; show it so the click never looks ignored.
  signingOut.value = true
  try {
    await signOutAndLeave()
  } catch {
    signingOut.value = false
  }
}

onMounted(() => {
  refresh()
  window.addEventListener('u80-session-changed', refresh)
})
onUnmounted(() => {
  window.removeEventListener('u80-session-changed', refresh)
})
watch(() => route.path, refresh)
</script>

<style scoped>
.navbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: var(--nav-height);
  padding: 0 1.25rem;
  background: var(--ink);
  color: var(--cream);
}

.brand {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  text-decoration: none;
  color: inherit;
}

.brand-mark {
  font-family: var(--font-display);
  font-weight: 700;
  color: var(--accent-soft);
  letter-spacing: 0.04em;
}

.brand-name {
  font-weight: 600;
  font-size: 0.95rem;
}

.links {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.session {
  font-size: 0.8rem;
  opacity: 0.75;
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav-link {
  color: var(--cream);
  text-decoration: none;
  font-weight: 500;
  opacity: 0.9;
}

.nav-link:hover,
.nav-link.router-link-active {
  opacity: 1;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.nav-guide {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.nav-guide svg {
  width: 1.05rem;
  height: 1.05rem;
}

.btn-logout {
  background: transparent;
  border: 1px solid rgba(250, 247, 241, 0.35);
  color: var(--cream);
  padding: 0.35rem 0.75rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}

.btn-logout:hover {
  background: rgba(250, 247, 241, 0.08);
}
</style>
